/**
 * What the credit-box screen found, in words a reader can act on.
 *
 * The dossier's screening panel showed "Program 1 — A Loans EXCEPTION" and a
 * list of raw field keys ("Missing stabilized_value — enter under Deal Inputs
 * → stabilized_value"), and never the check that actually failed — Delta
 * Yards' "loan_size EXCEPTION 18,000,000 vs 20,000,000–100,000,000" was on the
 * stored result and nowhere on screen. It sat four clicks deep, did not say
 * the ruleset is still awaiting sign-off, and the Underwriting dashboard's
 * Recommendation said only that no decision was recorded.
 *
 * Every check is listed with its outcome and detail, failing ones first, in
 * human labels; the ruleset's version and status are stated; and a one-line
 * verdict is exported for the Underwriting dashboard.
 */
import { useQuery } from '@tanstack/react-query'

import type { ScreenCheck, ScreenResults, ScreenRun } from '@/services/ingestion'
import { reconciliationService } from '@/services/reconciliation'

import { checkBasis, checkDetail } from './screeningBasis'

const LABELS: Record<string, string> = {
  loan_size: 'Loan size',
  loan_amount: 'Loan amount',
  asset_class: 'Asset class',
  geography: 'Geography',
  construction: 'Construction',
  max_ltv: 'Maximum LTV',
  min_debt_yield_stabilized: 'Minimum stabilized debt yield',
  min_dscr_amortizing: 'Minimum DSCR (amortizing)',
  stabilized_value: 'Stabilized value',
  stabilized_noi_or_ncf: 'Stabilized NOI or NCF',
}

const ACRONYMS: Record<string, string> = {
  ltv: 'LTV', ltc: 'LTC', dscr: 'DSCR', noi: 'NOI', ncf: 'NCF', sf: 'SF', dy: 'DY',
}

/** A check or field key as a reader would say it: "stabilized_noi_or_ncf" →
 *  "Stabilized NOI or NCF". Free text passes through unchanged. */
function screenLabel(key: string | null | undefined): string {
  const raw = String(key ?? '').trim()
  if (!raw) return ''
  if (LABELS[raw]) return LABELS[raw]
  if (!/^[a-z0-9]+(_[a-z0-9]+)+$/.test(raw)) return raw
  const words = raw.split('_').map((word) => ACRONYMS[word] ?? word)
  const first = words[0]
  return [first === first.toLowerCase() ? first.charAt(0).toUpperCase() + first.slice(1) : first, ...words.slice(1)].join(' ')
}

/** Field keys inside a sentence ("enter under Deal Inputs → stabilized_value"). */
function humanizeKeys(text: string | null | undefined): string {
  return String(text ?? '').replace(/\b[a-z0-9]+(?:_[a-z0-9]+)+\b/g, (key) => screenLabel(key))
}

const ORDER: Record<ScreenCheck['outcome'], number> = { EXCEPTION: 0, MISSING_INPUT: 1, REFER: 2, PASS: 3 }
const OUTCOME_TEXT: Record<ScreenCheck['outcome'], string> = {
  EXCEPTION: 'Exception',
  MISSING_INPUT: 'Missing input',
  REFER: 'For a person',
  PASS: 'Pass',
}
const OUTCOME_STYLE: Record<ScreenCheck['outcome'], string> = {
  EXCEPTION: 'border-red-500/30 text-red-300',
  MISSING_INPUT: 'border-amber-500/30 text-amber-300',
  REFER: 'border-sky-500/30 text-sky-300',
  PASS: 'border-emerald-500/25 text-emerald-300',
}

function sortedChecks(run: ScreenRun): ScreenCheck[] {
  return [...(run.checks ?? [])].sort((a, b) => (ORDER[a.outcome] ?? 9) - (ORDER[b.outcome] ?? 9))
}

/** Every check a program ran, what it found, failing ones first. */
export function ScreeningChecks({ run }: { run: ScreenRun }) {
  const checks = sortedChecks(run)
  if (checks.length === 0) {
    return <p className="mt-1 text-[10px] text-stone-500">No checks recorded on this run.</p>
  }
  return (
    <table className="mt-1 w-full table-fixed text-left text-[10px]" aria-label={`${run.programName || run.program} checks`}>
      <tbody className="divide-y divide-stone-900">
        {checks.map((check, index) => (
          <tr key={`${check.name}-${index}`}>
            <td className="w-[88px] py-1 pr-2 align-top">
              <span className={`inline-flex rounded border px-1 py-0.5 text-[8px] uppercase tracking-wide ${OUTCOME_STYLE[check.outcome] ?? ''}`}>
                {OUTCOME_TEXT[check.outcome] ?? check.outcome}
              </span>
            </td>
            <td className="w-[38%] py-1 pr-2 align-top text-stone-200">{screenLabel(check.name)}</td>
            <td className="py-1 align-top text-stone-400">
              {humanizeKeys(checkDetail(check.detail)) || '—'}
              {checkBasis(check, run) ? (
                <div className="text-[9px] text-stone-500" data-screen-basis>{checkBasis(check, run)}</div>
              ) : null}
              {check.outcome === 'MISSING_INPUT' && check.fillHint ? (
                <div className="text-[9px] text-stone-600">{humanizeKeys(check.fillHint)}</div>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Why the screen did not run, and what the package would need to supply. */
export function ScreeningNotRun({ screen }: { screen: ScreenResults }) {
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-stone-400">
        Screening was not run{screen.reason ? `: ${humanizeKeys(screen.reason)}` : '.'}
      </p>
      {(screen.missing ?? []).length > 0 && (
        <ul className="space-y-1 text-[11px] text-stone-400">
          {(screen.missing ?? []).map((m) => (
            <li key={m.field} className="rounded border border-stone-800 px-2 py-1.5">
              <span className="text-amber-200/80">{screenLabel(m.field)}</span>
              <span className="text-stone-500"> — {humanizeKeys(m.why)}</span>
              {m.fillHint && <div className="text-[10px] text-stone-600">{humanizeKeys(m.fillHint)}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The ruleset a screen ran against: version, sign-off status, and whether
 *  the rules have moved since this result was produced. */
export function ScreeningRuleset({ runs }: { runs: ScreenRun[] }) {
  const ruleset = useQuery({
    queryKey: ['credit-box-ruleset'],
    queryFn: reconciliationService.getCreditBox,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const version = runs.find((run) => run.rulesVersion)?.rulesVersion ?? ruleset.data?.rules.version
  const status = ruleset.data?.rules.status
  const stale = Boolean(
    ruleset.data?.artifact_sha256
      && runs.some((run) => run.rulesSha256 && run.rulesSha256 !== ruleset.data?.artifact_sha256),
  )
  if (!version && !status) return null
  return (
    <p className="text-[10px] text-stone-500" aria-label="Screening ruleset">
      Credit box {version ? `v${version}` : ''}
      {status ? <span className={status === 'signed_off' ? '' : 'text-amber-300/90'}> · {status.replace(/_/g, ' ').replace('sign off', 'sign-off')}</span> : null}
      {stale ? <span className="text-amber-300/90"> · screened against an earlier version of the rules</span> : null}
    </p>
  )
}

/** One line per program for the Underwriting dashboard: the outcome and the
 *  checks that decided it. */
export function ScreeningVerdict({ screen }: { screen: ScreenResults | null | undefined }) {
  if (!screen) return null
  if (screen.status === 'not_run') {
    return (
      <p className="mt-2 text-[9.5px] text-stone-500">
        Credit-box screen not run{screen.reason ? ` — ${humanizeKeys(screen.reason)}` : ''}.
      </p>
    )
  }
  const runs = screen.runs ?? []
  if (runs.length === 0) return null
  return (
    <div className="mt-2 space-y-1 text-[9.5px]" aria-label="Credit-box screen verdict">
      {runs.map((run, index) => {
        const deciding = sortedChecks(run).filter((check) =>
          run.outcome === 'EXCEPTION' ? check.outcome === 'EXCEPTION'
            : run.outcome === 'INCOMPLETE' ? check.outcome === 'MISSING_INPUT' : false)
        return (
          <div key={`${run.program}-${index}`} className="text-stone-400">
            <span className="text-stone-300">{run.programName || run.program}</span>
            {' · '}
            <span className={run.outcome === 'PASS' ? 'text-emerald-300' : run.outcome === 'EXCEPTION' ? 'text-red-300' : 'text-amber-300'}>
              {run.outcome === 'INCOMPLETE' ? 'Incomplete' : run.outcome === 'EXCEPTION' ? 'Exception' : 'Pass'}
            </span>
            {deciding.length > 0 ? (
              <span>
                {' — '}
                {deciding.map((check) => `${screenLabel(check.name)}${check.outcome === 'EXCEPTION' && check.detail ? `: ${humanizeKeys(check.detail)}` : ''}`).join('; ')}
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
